export const VENDOR_CATEGORY_TYPES = {
  contractor: 'Contractor',
  material: 'Material',
};

const normalizeText = (value = '') => value.toString().trim().toLowerCase();

const escapeSolrValue = (value = '') => value.toString().replace(/(["\\])/g, '\\$1');

const getTaxonomyByType = (taxonomyData = {}, type = '') => {
  if (!type) return null;
  return taxonomyData?.[type] || taxonomyData?.[Object.keys(taxonomyData || {}).find((key) => normalizeText(key) === normalizeText(type))];
};

export const getVendorCategoryType = (vendorType = '') => {
  const value = normalizeText(vendorType);
  if (value === 'material' || value.includes('material supplier')) return VENDOR_CATEGORY_TYPES.material;
  if (value === 'contractor' || value.includes('contractor') || value.includes('labour')) return VENDOR_CATEGORY_TYPES.contractor;
  return '';
};

export const buildVendorCategoryFilter = ({ type, disciplineId, categoryId, subCategoryId } = {}) => {
  const filters = [];
  const categoryType = getVendorCategoryType(type) || type;

  if (categoryType) filters.push(`vendorCategories.vendorType:("${escapeSolrValue(categoryType)}")`);
  if (disciplineId) filters.push(`vendorCategories.disciplines.disciplineId:("${escapeSolrValue(disciplineId)}")`);
  if (categoryId) filters.push(`vendorCategories.disciplines.categories.categoryId:("${escapeSolrValue(categoryId)}")`);
  if (subCategoryId) filters.push(`vendorCategories.disciplines.categories.subCategories.subCategoryId:("${escapeSolrValue(subCategoryId)}")`);

  return filters.join(' AND ');
};

const flattenTaxonomyType = (taxonomy = {}, categoryType = '') => {
  const items = [];

  (taxonomy?.disciplines || []).forEach((discipline) => {
    const disciplineId = discipline?._id ? String(discipline._id) : '';
    const disciplineName = discipline?.name || '';

    if (disciplineId && disciplineName) {
      items.push({
        level: 'discipline',
        type: categoryType,
        name: disciplineName,
        disciplineId,
        disciplineName,
      });
    }

    (discipline?.categories || []).forEach((category) => {
      const categoryId = category?._id ? String(category._id) : '';
      const categoryName = category?.name || '';

      if (disciplineId && categoryId && categoryName) {
        items.push({
          level: 'category',
          type: categoryType,
          name: categoryName,
          disciplineId,
          disciplineName,
          categoryId,
          categoryName,
        });
      }

      (category?.subCategories || []).forEach((subCategory) => {
        const subCategoryId = subCategory?._id ? String(subCategory._id) : '';
        const subCategoryName = subCategory?.name || '';

        if (disciplineId && categoryId && subCategoryId && subCategoryName) {
          items.push({
            level: 'subCategory',
            type: categoryType,
            name: subCategoryName,
            disciplineId,
            disciplineName,
            categoryId,
            categoryName,
            subCategoryId,
          });
        }
      });
    });
  });

  return items;
};

export const flattenVendorTaxonomy = (taxonomyData = {}, type = '') => {
  const categoryType = getVendorCategoryType(type) || type;

  if (!categoryType) {
    return Object.entries(taxonomyData || {}).flatMap(([key, taxonomy]) =>
      flattenTaxonomyType(taxonomy, key),
    );
  }

  return flattenTaxonomyType(getTaxonomyByType(taxonomyData, categoryType), categoryType);
};

export const findTaxonomyMatch = (taxonomyData = {}, type = '', searchTerm = '') => {
  const term = normalizeText(searchTerm);
  if (!term) return null;

  const items = flattenVendorTaxonomy(taxonomyData, type);
  const priority = { subCategory: 0, category: 1, discipline: 2 };
  const sortedItems = [...items].sort((left, right) => priority[left.level] - priority[right.level]);

  return (
    sortedItems.find((item) => normalizeText(item.name) === term) ||
    sortedItems.find((item) => normalizeText(item.name).startsWith(term)) ||
    sortedItems.find((item) => normalizeText(item.name).includes(term)) ||
    null
  );
};

export const findTaxonomySuggestions = (taxonomyData = {}, type = '', searchTerm = '', limit = 8) => {
  const term = normalizeText(searchTerm);
  if (!term) return [];

  const priority = { subCategory: 0, category: 1, discipline: 2 };
  const scoreMatch = (item) => {
    const name = normalizeText(item.name);
    if (name === term) return 0;
    if (name.startsWith(term)) return 1;
    if (name.includes(term)) return 2;
    return 3;
  };

  return flattenVendorTaxonomy(taxonomyData, type)
    .filter((item) => scoreMatch(item) < 3)
    .sort((left, right) => {
      const scoreDifference = scoreMatch(left) - scoreMatch(right);
      if (scoreDifference !== 0) return scoreDifference;
      return priority[left.level] - priority[right.level];
    })
    .slice(0, limit);
};